import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'soft' | 'ghost' | 'danger' | 'sun';
type Size = 'md' | 'lg' | 'xl';

const base =
  'inline-flex items-center justify-center gap-2 font-extrabold select-none rounded-[var(--radius-button)] transition-[transform,box-shadow,background-color] duration-150 active:translate-y-[3px] disabled:opacity-50 disabled:active:translate-y-0 text-center';

const variants: Record<Variant, string> = {
  primary:
    'bg-brand-700 text-white shadow-[0_4px_0_var(--color-brand-800)] active:shadow-[0_1px_0_var(--color-brand-800)] hover:bg-brand-800',
  sun: 'bg-sun-300 text-ink shadow-[0_4px_0_var(--color-sun-500)] active:shadow-[0_1px_0_var(--color-sun-500)] hover:bg-sun-500',
  secondary:
    'bg-surface text-ink border-2 border-line shadow-[0_4px_0_var(--color-line)] active:shadow-[0_1px_0_var(--color-line)] hover:bg-surface-muted',
  soft: 'bg-brand-50 text-brand-800 hover:bg-brand-100 active:translate-y-0',
  ghost: 'bg-transparent text-ink-muted hover:bg-surface-muted active:translate-y-0',
  danger:
    'bg-coral-700 text-white shadow-[0_4px_0_#7a2614] active:shadow-[0_1px_0_#7a2614] hover:bg-[#8e2c17]',
};

const sizes: Record<Size, string> = {
  md: 'min-h-12 px-4 text-base',
  lg: 'min-h-14 px-6 text-lg',
  xl: 'min-h-16 px-8 text-xl',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', block, icon, className, children, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(base, variants[variant], sizes[size], block && 'w-full', className)}
      {...props}
    >
      {icon}
      {children}
    </button>
  );
});

export interface ButtonLinkProps extends LinkProps {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  icon?: ReactNode;
}

export function ButtonLink({
  variant = 'primary',
  size = 'md',
  block,
  icon,
  className,
  children,
  ...props
}: ButtonLinkProps) {
  return (
    <Link
      className={cn(base, variants[variant], sizes[size], block && 'w-full', className)}
      {...props}
    >
      {icon}
      {children}
    </Link>
  );
}
